import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Table, Modal, ModalBody, ModalHeader, ModalFooter } from 'react-bootstrap';
import { FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import axiosInstance from "../../api/axiosConfig.jsx";
import {jwtDecode} from 'jwt-decode';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import nl from 'date-fns/locale/nl';
registerLocale('nl', nl);



const ExpenseCalculator = () => {
    const [timeRecords, setTimeRecords] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [corrections, setCorrections] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [expenses, setExpenses] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [modalIcon, setModalIcon] = useState(null);
    const [employeeExpenses, setEmployeeExpenses] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    useEffect(() => {
        const token = sessionStorage.getItem('authToken');
        if (token) {
            const decodedToken = jwtDecode(token);
            setCompanyId(decodedToken.companyId);
        } else {
            setError("No valid auth token found.");
        }
    }, []);

    useEffect(() => {
        if (companyId) {
            fetchData();
        }
    }, [companyId, selectedDate]);

    const fetchData = async () => {
        if (!companyId) {
            return;
        }

        const startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

        try {
            const [timeResponse, correctionsResponse, schedulesResponse, employeesResponse] = await Promise.all([
                axiosInstance.get(`/time/company/${companyId}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                axiosInstance.get(`/corrections/all/company/${companyId}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                axiosInstance.get(`/schedules/company/${companyId}`),
                axiosInstance.get(`/employees/company/${companyId}`)
            ]);

            setTimeRecords(timeResponse.data);
            setCorrections(correctionsResponse.data.filter(correction => correction.status === 'APPROVED'));
            setSchedules(schedulesResponse.data);
            setEmployees(employeesResponse.data);
        } catch (err) {
            setError('Er is een fout opgetreden bij het ophalen van de gegevens.');
            showErrorModal('Fout', 'Er is een fout opgetreden bij het ophalen van de gegevens.');

        }
    };

    const exportToCSV = () => {
        const headers = ["Medewerker", "Gewerkte uren", "Loonkosten"];
        const rows = employeeExpenses.map((employee) => [
            `${employee.firstName} ${employee.lastName}`,
            `${employee.hoursWorked} hrs`,
            `€${employee.expense}`
        ]);

        let csvContent = "data:text/csv;charset=utf-8,"
            + [headers, ...rows].map(e => e.join(",")).join("\n");

        const monthName = selectedDate.toLocaleString('en-US', { month: 'long' });
        const year = selectedDate.getFullYear();
        const fileName = `expense_report_${monthName}_${year}.csv`;

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const calculateExpenses = () => {
        let totalExpenses = 0;
        const employeeMap = {};

        const startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

        const relevantTimeRecords = timeRecords.filter(record => {
            const clockIn = new Date(record.clockInTime);
            return clockIn >= startDate && clockIn <= endDate;
        });

        if (relevantTimeRecords.length === 0) {
            showErrorModal('Geen gegevens', 'Geen relevante tijdregistraties gevonden voor de geselecteerde periode.');
            return;
        }

        relevantTimeRecords.forEach(record => {
            if (record.approvalStatus === 'Approved') {
                let clockIn = new Date(record.clockInTime);
                let clockOut = new Date(record.clockOutTime);
                let duration = (clockOut - clockIn) / (1000 * 60 * 60);

                const relevantCorrection = corrections.find(correction =>
                    correction.employeeEmail === record.employee.email &&
                    new Date(correction.requestedTime).toDateString() === clockOut.toDateString()
                );

                if (relevantCorrection) {
                    clockOut = new Date(relevantCorrection.requestedTime);
                    duration = (clockOut - clockIn) / (1000 * 60 * 60);
                }

                const breakTime = calculateBreakTime(duration);
                const durationWithBreak = duration - breakTime;

                const employee = employees.find(emp => emp.email === record.employee.email);
                const hourlyRate = employee ? employee.salary * employee.hoursRating : 0;

                const expense = durationWithBreak * hourlyRate;
                totalExpenses += expense;

                if (employeeMap[record.employee.email]) {
                    employeeMap[record.employee.email].expense += expense;
                    employeeMap[record.employee.email].hoursWorked += durationWithBreak;
                } else {
                    employeeMap[record.employee.email] = {
                        email: record.employee.email,
                        firstName: employee.firstName,
                        lastName: employee.lastName,
                        hourlyRate: hourlyRate,
                        expense: expense,
                        hoursWorked: durationWithBreak
                    };
                }
            }
        });

        const employeeDetails = Object.values(employeeMap).map(employee => ({
            ...employee,
            expense: employee.expense.toFixed(2),
            hoursWorked: employee.hoursWorked.toFixed(2)
        }));

        setExpenses(totalExpenses.toFixed(2));
        setEmployeeExpenses(employeeDetails);
        showSuccessModal('Kostenberekening voltooid', `Totale kosten voor de geselecteerde periode zijn €${totalExpenses.toFixed(2)}.`);
    };

    const saveExpensesToDatabase = async () => {
        if (!companyId) {
            showErrorModal('Error', 'No valid company found.');
            return;
        }

        try {
            const expenseData = {
                companyId: companyId,
                totalExpense: parseFloat(expenses),
                startDate: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).toISOString(),
                endDate: new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).toISOString()
            };

            await axiosInstance.post('/expenses/calculate', expenseData);
            showSuccessModal('Gegevens Opgeslagen', 'De onkosten zijn succesvol opgeslagen in de database.');
        } catch (err) {
            showErrorModal('Opslaan Mislukt', 'Er is een fout opgetreden bij het opslaan van de onkosten.');

        }
    };

    const calculateBreakTime = (workDuration) => {
        if (workDuration <= 5.5) return 0;
        if (workDuration <= 8) return 0.5;
        if (workDuration <= 10) return 0.75;
        return 1;
    };

    const showErrorModal = (title, message) => {
        setModalTitle(title);
        setModalMessage(message);
        setModalIcon(<FaExclamationCircle className="text-danger" size={24} />);
        setShowModal(true);
    };

    const showSuccessModal = (title, message) => {
        setModalTitle(title);
        setModalMessage(message);
        setModalIcon(<FaCheckCircle className="text-success" size={24} />);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    return (
        <Container className="my-5">
            <h1 className="mb-4">Loonkosten</h1>
            {error && <div className="alert alert-danger mt-3">{error}</div>}

            {/* Filterbalk */}
            <div className="card mb-4 shadow-sm">
                <div className="card-body">
                    <Row className="align-items-end">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold">Selecteer Maand</Form.Label>
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={(date) => setSelectedDate(date)}
                                    showMonthYearPicker
                                    dateFormat="MMMM yyyy"
                                    className="form-control w-100"
                                    locale="nl" // Nederlandse datums
                                />
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <div className="d-flex gap-2 justify-content-end">
                                <Button variant="primary" onClick={calculateExpenses}>
                                    Bereken Kosten
                                </Button>
                                <Button variant="success" onClick={saveExpensesToDatabase}>
                                    Opslaan
                                </Button>
                                <Button variant="outline-secondary" onClick={exportToCSV}>
                                    Exporteer (CSV)
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Resultaten Tabel */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                        <tr>
                            <th>Medewerker</th>
                            <th className="text-end">Gewerkte Uren</th>
                            <th className="text-end">Kosten</th>
                        </tr>
                        </thead>
                        <tbody>
                        {employeeExpenses.map((employee, index) => (
                            <tr key={index}>
                                <td>{employee.firstName} {employee.lastName}</td>
                                <td className="text-end">{employee.hoursWorked} uur</td>
                                <td className="text-end">€ {employee.expense}</td>
                            </tr>
                        ))}
                        {employeeExpenses.length > 0 && (
                            <tr className="table-active fw-bold">
                                <td colSpan="2" className="text-end">Totale Kosten:</td>
                                <td className="text-end">€ {expenses}</td>
                            </tr>
                        )}
                        {employeeExpenses.length === 0 && (
                            <tr>
                                <td colSpan="3" className="text-center text-muted py-4">
                                    Geen gegevens beschikbaar voor de geselecteerde periode
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <ModalHeader closeButton className="border-bottom-0">
                    <div className="d-flex align-items-center">
                        {modalIcon}
                        <span className="ms-2 fw-bold">{modalTitle}</span>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-0">
                    <p className="mb-0">{modalMessage}</p>
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <Button variant="primary" onClick={handleCloseModal}>
                        Sluiten
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default ExpenseCalculator;